import { MapPin, Phone, Mail, Clock, Navigation, MessageCircle, Receipt } from "lucide-react";
import SEO from "@/components/SEO";
import EnquiryForm from "@/components/EnquiryForm";
import { Reveal, Kicker } from "@/components/Reveal";
import { useSettings } from "@/context/SettingsContext";
import { telLink, waLink } from "@/lib/api";

export default function Contact() {
  const s = useSettings();
  return (
    <>
      <SEO
        title="Contact | Hari Om Trading Co. — Tahliwal, Una, Himachal Pradesh"
        description="Contact Hari Om Trading Co., Garhshankar–Nangal Road, Near UCO Bank, Tahliwal, Himachal Pradesh 174507. Call +91 82792 21172 or send an enquiry."
      />
      <section className="grain bg-[#111111] bg-grid-dark py-16 lg:py-24" data-testid="contact-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <Kicker dark>Contact</Kicker>
            <h1 className="mt-4 font-heading text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Contact Us
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F6F7F8] py-16 lg:py-24">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-2">
          <Reveal>
            <div className="space-y-5">
              <div className="bg-[#111111] p-8" data-testid="contact-info-card">
                <h2 className="font-heading text-2xl font-bold text-white">{s.business_name}</h2>
                <p className="mt-1 text-sm text-[#F5B700]">{s.tagline}</p>
                <ul className="mt-7 space-y-5 text-sm text-[#c9cdd4]">
                  <li className="flex gap-3">
                    <MapPin size={18} className="mt-0.5 shrink-0 text-[#F5B700]" aria-hidden="true" />
                    <span data-testid="contact-address">{s.address_line1},<br />{s.address_line2}</span>
                  </li>
                  <li className="flex gap-3">
                    <Phone size={18} className="mt-0.5 shrink-0 text-[#F5B700]" aria-hidden="true" />
                    <span>
                      <a href={telLink(s)} data-testid="contact-phone" className="font-semibold text-white hover:text-[#F5B700]">{s.phone}</a>
                      <br />Secondary: {s.secondary_phone}
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <Mail size={18} className="mt-0.5 shrink-0 text-[#F5B700]" aria-hidden="true" />
                    <span data-testid="contact-email">{s.email}</span>
                  </li>
                  <li className="flex gap-3">
                    <Clock size={18} className="mt-0.5 shrink-0 text-[#F5B700]" aria-hidden="true" />
                    <span data-testid="contact-hours">{s.hours_weekday}<br />{s.hours_sunday}</span>
                  </li>
                  {s.gstin ? (
                    <li className="flex gap-3">
                      <Receipt size={18} className="mt-0.5 shrink-0 text-[#F5B700]" aria-hidden="true" />
                      <span>GSTIN: <strong className="text-white" data-testid="contact-gstin">{s.gstin}</strong><br />Registered taxable business — GST invoice available</span>
                    </li>
                  ) : null}
                </ul>
                <div className="mt-8 grid grid-cols-3 gap-2.5">
                  <a href={s.maps_url} target="_blank" rel="noopener noreferrer" data-testid="contact-directions" className="flex min-h-[48px] items-center justify-center gap-1.5 bg-[#F5B700] px-3 py-3 text-sm font-semibold text-[#111111] hover:bg-[#D99C00]">
                    <Navigation size={15} aria-hidden="true" /> Directions
                  </a>
                  <a href={telLink(s)} data-testid="contact-call-btn" className="flex min-h-[48px] items-center justify-center gap-1.5 border border-[#3a3d42] px-3 py-3 text-sm font-semibold text-white hover:border-[#F5B700] hover:text-[#F5B700]">
                    <Phone size={15} aria-hidden="true" /> Call
                  </a>
                  <a href={waLink(s)} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn" className="flex min-h-[48px] items-center justify-center gap-1.5 border border-[#3a3d42] px-3 py-3 text-sm font-semibold text-white hover:border-[#18864B] hover:text-[#4ade80]">
                    <MessageCircle size={15} aria-hidden="true" /> WhatsApp
                  </a>
                </div>
              </div>
              <div className="overflow-hidden border border-[#D9DDE3]" data-testid="contact-map">
                <iframe
                  title="Hari Om Trading Co. location map"
                  src={s.maps_embed}
                  className="h-[320px] w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="border border-[#D9DDE3] bg-white p-6 sm:p-10" data-testid="contact-form-card">
              <h2 className="font-heading text-2xl font-bold text-[#161616]">Send an Enquiry</h2>
              <p className="mb-7 mt-2 text-sm text-[#667085]">
                Share your requirement and contact details below.
              </p>
              <EnquiryForm source="contact" compact />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
